'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

/**
 * Props for `CenteredIntroText`.
 */
export type CenteredIntroTextProps =
  SliceComponentProps<Content.CenteredIntroTextSlice>;

/**
 * Component for "CenteredIntroText" Slices.
 * Displays a centered intro with heading, bullet points, and supporting text.
 */
const CenteredIntroText: FC<CenteredIntroTextProps> = ({ slice }) => {
  const { title, supporting_text } = slice.primary;
  const bulleted_list =
    slice.variation === 'default' ? slice.primary.bulleted_list : undefined;
  const transparent = Boolean(
    (slice.primary as { transparent_bg?: boolean }).transparent_bg,
  );

  const titleRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-bottom');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, observerOptions);

    const elements = [titleRef.current, listRef.current, textRef.current];

    elements.forEach((element, index) => {
      if (element) {
        element.classList.add(
          'opacity-0',
          'translate-y-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        element.style.transitionDelay = `${index * 150}ms`;
        observer.observe(element);
      }
    });

    return () => {
      elements.forEach((element) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-[150px]"
      style={
        transparent
          ? undefined
          : {
              backgroundColor:
                slice.variation === 'default' ? '#0e1010' : '#0F1211',
            }
      }
    >
      <div className="max-w-[1140px] mx-auto">
        <div className="flex flex-col gap-6 items-center text-center">
          {/* Title */}
          {slice.variation === 'default' && title && (
            <div ref={titleRef} className="w-full max-w-[1040px]">
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-3xl md:text-4xl lg:text-[42px] font-medium text-white leading-[1.2]">
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
          )}
          {slice.variation === 'highlighted' && title && (
            <div ref={titleRef} className="w-full max-w-[1040px]">
              <h2 className="text-3xl md:text-4xl lg:text-[42px] font-medium text-white leading-[1.2]">
                <HighlightedText
                  text={asText(title)}
                  highlightClassName="text-[#05F273]"
                />
              </h2>
            </div>
          )}

          {/* Bulleted List */}
          {bulleted_list && bulleted_list.length > 0 && (
            <ul
              ref={listRef}
              className="list-disc list-inside text-lg text-white leading-[1.5] max-w-[746px]"
            >
              {bulleted_list.map((item, index) => (
                <li key={index} className="mb-0">
                  <span>{item.point}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Supporting Text */}
          {supporting_text && (
            <div
              ref={textRef}
              className="text-lg text-white leading-[1.5] max-w-[746px]"
            >
              <PrismicRichText
                field={supporting_text}
                components={{
                  paragraph: ({ children }) => (
                    <p className="mt-4">{children}</p>
                  ),
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CenteredIntroText;
